(function () {
  const SKEW_TRANSITION_DURATION = 1000;

  const playVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const prevSlide = swiper.slides[swiper.previousIndex];
    const activeSlide = swiper.slides[swiper.activeIndex];

    if (activeSlide) {
      // Shopify hosted video
      const videoActive = activeSlide.querySelector(
        "video.slideshow-slide__video"
      );
      if (videoActive) {
        videoActive.play().catch((error) => {
          console.error("Error playing video:", error);
        });
      }

      // Embedded video (Youtube or Vimeo)
      const videoUrlActive = activeSlide.querySelector(
        "iframe.slideshow-slide__video"
      );
      if (videoUrlActive) {
        if (videoUrlActive.classList.contains("js-youtube")) {
          videoUrlActive.contentWindow.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            "*"
          );
        } else if (videoUrlActive.classList.contains("js-vimeo")) {
          videoUrlActive.contentWindow.postMessage('{"method":"play"}', "*");
        }
      }
    }

    if (prevSlide && prevSlide !== activeSlide) {
      // Shopify hosted video
      const videoPrev = prevSlide.querySelector("video.slideshow-slide__video");
      if (videoPrev) videoPrev.pause();

      // Embedded video (Youtube or Vimeo)
      const videoUrlPrev = prevSlide.querySelector(
        "iframe.slideshow-slide__video"
      );
      if (videoUrlPrev) {
        if (videoUrlPrev.classList.contains("js-youtube")) {
          videoUrlPrev.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            "*"
          );
        } else if (videoUrlPrev.classList.contains("js-vimeo")) {
          videoUrlPrev.contentWindow.postMessage('{"method":"pause"}', "*");
        }
      }
    }
  };

  const stopVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const activeSlide = swiper.slides[swiper.activeIndex];

    if (activeSlide) {
      // Shopify hosted video
      const videoActive = activeSlide.querySelector(
        "video.slideshow-slide__video"
      );
      if (videoActive) videoActive.pause();

      // Embedded video (Youtube or Vimeo)
      const videoUrlActive = activeSlide.querySelector(
        "iframe.slideshow-slide__video"
      );
      if (videoUrlActive) {
        if (videoUrlActive.classList.contains("js-youtube")) {
          videoUrlActive.contentWindow.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            "*"
          );
        } else if (videoUrlActive.classList.contains("js-vimeo")) {
          videoUrlActive.contentWindow.postMessage('{"method":"pause"}', "*");
        }
      }
    }
  };

  const initAutoplayObserver = (section, sliderEl) => {
    if (!section || !section?.classList.contains("slideshow-section")) return;
    if (!sliderEl || !sliderEl.swiper) return;

    const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (hasAutoplay) sliderEl.swiper.autoplay.resume();
          playVideo(sliderEl.swiper);
        } else {
          if (hasAutoplay) sliderEl.swiper.autoplay.pause();
          stopVideo(sliderEl.swiper);
        }
      });
    });

    sectionObserver.observe(section);
  };

  const initSlideshow = (section) => {
    if (!section || !section?.classList.contains("slideshow-section")) return;

    const sliderEl = section.querySelector(".js-slideshow-swiper");
    if (!sliderEl) return;

    if (sliderEl.swiper) sliderEl.swiper.destroy();

    const animationType = sliderEl.dataset?.animationType || "slide";
    const isSkew = animationType === "skew";
    const paginationEl = section.querySelector(".slider-pagination");
    const nextBtn = section.querySelector(".slider-nav-button--next");
    const prevBtn = section.querySelector(".slider-nav-button--prev");
    const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
    const hasSliderAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
    const pauseAutoplayOnHover =
      sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
    const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 5000);
    const hasParallax = sliderEl.getAttribute("data-parallax") === "true";
    const sectionWidth =
      sliderEl.getAttribute("data-section-width") || "standard";

    let skewCleanupTimer = null;

    const cleanupSkewSlides = (slides) => {
      slides.forEach((slide) => {
        slide.classList.remove(
          "is-entering-next",
          "is-entering-prev",
          "is-leaving"
        );
      });
    };

    const scheduleSkewCleanup = (s) => {
      clearTimeout(skewCleanupTimer);
      skewCleanupTimer = setTimeout(() => {
        cleanupSkewSlides(s.slides);
        s.animating = false;
        s.wrapperEl.dispatchEvent(new Event("transitionend"));
      }, SKEW_TRANSITION_DURATION + 50);
    };

    const onSkewTransitionStart = (s, direction) => {
      const incoming = s.slides[s.activeIndex];
      const leaving = s.slides[s.previousIndex];

      cleanupSkewSlides(s.slides);

      if (leaving && leaving !== incoming) {
        leaving.classList.add("is-leaving");
      }

      if (incoming) {
        void incoming.offsetWidth;
        incoming.classList.add(
          direction === "next" ? "is-entering-next" : "is-entering-prev"
        );
      }

      playVideo(s);
      setTimeout(() => {
        s.animating = true;
      });

      scheduleSkewCleanup(s);
    };

    const swiperOptions = {
      loop: sliderLoop,
      slidesPerView: 1,
      spaceBetween: isSkew ? 0 : sectionWidth === "standard" ? 12 : 0,
      speed: isSkew ? 0 : animationType === "creative" ? 1000 : 800,
      watchSlideProgress: true,
      allowTouchMove: true,
      resistanceRatio: isSkew ? 0 : animationType === "creative" ? 0 : 0.85,
      parallax: hasParallax,
      mousewheel: {
        forceToAxis: true,
      },
      autoplay: hasSliderAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: pauseAutoplayOnHover,
          }
        : false,
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      breakpoints: {
        576: {
          spaceBetween: isSkew ? 0 : sectionWidth === "standard" ? 20 : 0,
        },
      },
      on: {
        init(s) {
          if (isSkew) {
            const first = s.slides[s.activeIndex];
            if (first) first.classList.add("is-active");
          }
        },
        autoplayTimeLeft(s, time, progress) {
          if (paginationEl) {
            paginationEl.style.setProperty("--slider-progress", 1 - progress);
          }
        },
        slideChange(s) {
          if (!isSkew) return;

          const active = s.slides[s.activeIndex];
          s.slides.forEach((slide) => {
            if (slide !== active) slide.classList.remove("is-active");
          });
          if (active) active.classList.add("is-active");
        },
        slideNextTransitionStart(s) {
          if (isSkew) onSkewTransitionStart(s, "next");
        },
        slidePrevTransitionStart(s) {
          if (isSkew) onSkewTransitionStart(s, "prev");
        },
        slideChangeTransitionStart() {
          if (animationType === "creative") {
            this.allowSlideNext = false;
            this.allowSlidePrev = false;
            this.allowTouchMove = false;

            const prevSlide = this.slides[this.previousIndex];
            const prevContent = prevSlide?.querySelector(
              ".slideshow-slide__content"
            );
            if (prevContent) {
              prevContent.style.transition = "opacity 0.3s ease";
              prevContent.style.opacity = "0";
            }

            playVideo(this);
          }
        },
        slideChangeTransitionEnd() {
          if (animationType === "creative") {
            this.allowSlideNext = true;
            this.allowSlidePrev = true;
            this.allowTouchMove = true;

            const prevSlide = this.slides[this.previousIndex];
            const prevContent = prevSlide?.querySelector(
              ".slideshow-slide__content"
            );
            if (prevContent) {
              prevContent.removeAttribute("style");
            }
          }
        },
      },
    };

    if (isSkew) {
      swiperOptions.virtualTranslate = true;
    } else {
      swiperOptions.effect = animationType;
      swiperOptions.creativeEffect =
        animationType === "creative"
          ? {
              prev: {
                shadow: true,
                translate: ["-20%", 0, -1],
              },
              next: {
                translate: ["100%", 0, 0],
              },
            }
          : {};
    }

    new Swiper(sliderEl, swiperOptions);

    initAutoplayObserver(section, sliderEl);
  };

  initSlideshow(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", function (event) {
    initSlideshow(event.target);
  });
})();
